import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    supplierSegmentationOverrideUrl,
    supplierSegmentationUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {SegmentationAxes, SegmentationScore, SegmentationType} from "~/pages/supplier-360/types/Supplier360Types";

export type OverrideSupplierSegmentationVariables = SegmentationAxes & {
    /** `SupplierEntity` id — the action is keyed by the entity, not by a `SegmentationScore` id. */
    entityId: number;
    /**
     * The quadrant the scorer picked. Sent because the design makes it the scorer's own call;
     * **currently ignored server-side**, which still derives it from spend value × supply risk
     * (docs §2.12).
     */
    segment: SegmentationType;
    /** Omit for an entity's first score — there is nothing being overridden yet to justify. */
    reason?: string;
};

/**
 * `POST supplier_360/segmentation/override/` — docs §2.12. The one segmentation write, covering
 * both an entity's first score and a re-score of an existing one, since it is keyed by `entity`
 * in the body rather than by a score id in the path.
 *
 * `reason` is mandatory as soon as a prior score exists: the server answers
 * `400 {"reason": "required for a manual override"}` without one. All five axes are always sent,
 * so the first-score case (where the server has no prior value to fall back to and requires them)
 * is covered too. The matching `segmentation_audit` row is written server-side — nothing here is
 * derived client-side.
 */
export function useOverrideSupplierSegmentationMutation() {
    const queryClient = useQueryClient();

    return useMutation<SegmentationScore, DefaultError, OverrideSupplierSegmentationVariables>({
        mutationFn: async (variables) => {
            const {data} = await queryHttp.post<SegmentationScore>(
                supplierSegmentationOverrideUrl(),
                {
                    entity: variables.entityId,
                    segment: variables.segment,
                    spend_value: variables.spend_value,
                    supply_risk: variables.supply_risk,
                    switching_difficulty: variables.switching_difficulty,
                    substitutes: variables.substitutes,
                    business_criticality: variables.business_criticality,
                    ...(variables.reason ? {reason: variables.reason} : {}),
                },
            );
            return data;
        },
        onSuccess: (updated, {entityId, reason}) => {
            queryClient.setQueryData([supplierSegmentationUrl(entityId)], updated);
            notificationStore.show(reason ? 'Segmentation updated.' : 'Segmentation saved.', {
                severity: 'success',
                id: `segmentation-override-success-${entityId}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {entityId}) => {
            notificationStore.show("Could not save the segmentation. Please try again.", {
                severity: 'error',
                id: `segmentation-override-error-${entityId}`,
                autoHideDuration: 4000,
            });
        },
    });
}
